import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { googleDriveService } from '../../../services/googleDriveService';

const GoogleDriveFolderBrowser = ({ selectedFolders, onFolderSelect, folderType }) => {
  const [expandedFolders, setExpandedFolders] = useState(['root']);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkSignInStatus();
  }, []);

  const checkSignInStatus = async () => {
    try {
      const signedIn = await googleDriveService?.checkSignInStatus();
      setIsSignedIn(signedIn);
      if (signedIn) {
        await loadRootFolders();
      }
    } catch (err) {
      console.error('Error checking sign-in status:', err);
    }
  };

  const loadRootFolders = async () => {
    setLoading(true);
    setError(null);
    try {
      const rootFolders = await googleDriveService?.listFolders('root');
      
      // Get file counts for each folder
      const foldersWithDetails = await Promise.all(
        rootFolders?.map(async (folder) => {
          try {
            const details = await googleDriveService?.getFolderDetails(folder?.id);
            return {
              id: folder?.id,
              name: folder?.name,
              type: 'folder',
              thumbnail: folder?.thumbnailLink,
              thumbnailAlt: `Folder: ${folder?.name}`,
              fileCount: details?.fileCount,
              children: null // Will be loaded on expand
            };
          } catch (err) {
            return {
              id: folder?.id,
              name: folder?.name,
              type: 'folder',
              thumbnailAlt: `Folder: ${folder?.name}`,
              fileCount: 0,
              children: null
            };
          }
        })
      );

      setFolders([{
        id: 'root',
        name: 'My Drive',
        type: 'root',
        children: foldersWithDetails
      }]);
    } catch (err) {
      console.error('Error loading folders:', err);
      setError('Failed to load folders from Google Drive');
    } finally {
      setLoading(false);
    }
  };

  const loadSubfolders = async (folderId) => {
    try {
      const subfolders = await googleDriveService?.listFolders(folderId);
      
      const subfoldersWithDetails = await Promise.all(
        subfolders?.map(async (folder) => {
          try {
            const details = await googleDriveService?.getFolderDetails(folder?.id);
            return {
              id: folder?.id,
              name: folder?.name,
              type: 'folder',
              thumbnail: folder?.thumbnailLink,
              thumbnailAlt: `Folder: ${folder?.name}`,
              fileCount: details?.fileCount,
              children: null
            };
          } catch (err) {
            return {
              id: folder?.id,
              name: folder?.name,
              type: 'folder',
              thumbnailAlt: `Folder: ${folder?.name}`,
              fileCount: 0,
              children: null
            };
          }
        })
      );

      return subfoldersWithDetails;
    } catch (err) {
      console.error('Error loading subfolders:', err);
      return [];
    }
  };

  const handleConnect = async () => {
    setIsConnecting(true);
    setError(null);
    try {
      await googleDriveService?.signIn();
      setIsSignedIn(true);
      await loadRootFolders();
    } catch (err) {
      console.error('Error connecting to Google Drive:', err);
      setError('Failed to connect to Google Drive. Please try again.');
    } finally {
      setIsConnecting(false);
    }
  };

  const toggleFolder = async (folderId) => {
    const isExpanded = expandedFolders?.includes(folderId);
    
    if (isExpanded) {
      // Collapse folder
      setExpandedFolders((prev) => prev?.filter((id) => id !== folderId));
    } else {
      // Expand folder and load children if not loaded
      setExpandedFolders((prev) => [...prev, folderId]);
      
      // Find folder and load children if needed
      const updateFolderChildren = (folderList) => {
        return folderList?.map(folder => {
          if (folder?.id === folderId && folder?.children === null) {
            // Load children
            loadSubfolders(folderId)?.then(children => {
              setFolders(prevFolders => updateFolderInTree(prevFolders, folderId, children));
            });
            return { ...folder, children: [] }; // Temporary empty array while loading
          } else if (folder?.children) {
            return { ...folder, children: updateFolderChildren(folder?.children) };
          }
          return folder;
        });
      };
      
      setFolders(prevFolders => updateFolderChildren(prevFolders));
    }
  };

  const updateFolderInTree = (folderList, targetId, newChildren) => {
    return folderList?.map(folder => {
      if (folder?.id === targetId) {
        return { ...folder, children: newChildren };
      } else if (folder?.children) {
        return { ...folder, children: updateFolderInTree(folder?.children, targetId, newChildren) };
      }
      return folder;
    });
  };

  const renderFolder = (folder, level = 0) => {
    const isExpanded = expandedFolders?.includes(folder?.id);
    const isSelected = selectedFolders?.[folderType] === folder?.id;
    const hasChildren = folder?.children && folder?.children?.length > 0;
    const canHaveChildren = folder?.type === 'root' || folder?.type === 'folder';

    return (
      <div key={folder?.id} className="w-full">
        <div
          className={`
            flex items-center gap-3 p-3 rounded-lg cursor-pointer
            transition-all duration-200 hover:bg-muted
            ${isSelected ? 'bg-primary/10 border border-primary' : 'border border-transparent'}
          `}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
          onClick={() => {
            if (folder?.type !== 'root') {
              onFolderSelect(folderType, folder?.id, folder?.name);
            }
            if (canHaveChildren) {
              toggleFolder(folder?.id);
            }
          }}>

          {canHaveChildren &&
          <Icon
            name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
            size={16}
            className="flex-shrink-0" />

          }
          
          {folder?.thumbnail ?
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg overflow-hidden flex-shrink-0">
              <Image
              src={folder?.thumbnail}
              alt={folder?.thumbnailAlt}
              className="w-full h-full object-cover" />

            </div> :

          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
              <Icon name="Folder" size={20} color="var(--color-primary)" />
            </div>
          }

          <div className="flex-1 min-w-0">
            <p className="text-sm md:text-base font-medium truncate">{folder?.name}</p>
            {folder?.fileCount !== undefined &&
            <p className="text-xs text-muted-foreground">{folder?.fileCount} files</p>
            }
          </div>

          {isSelected &&
          <Icon name="CheckCircle2" size={20} color="var(--color-primary)" />
          }
        </div>
        {isExpanded && hasChildren &&
        <div className="mt-1">
            {folder?.children?.map((child) => renderFolder(child, level + 1))}
          </div>
        }
      </div>);

  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm md:text-base font-semibold">
            {folderType === 'highRes' ? 'High Resolution Folder' : 'Compressed Files Folder'}
          </h4>
          <p className="text-xs md:text-sm text-muted-foreground mt-1">
            {folderType === 'highRes' ? 'Select folder containing original high-resolution files' : 'Select folder containing compressed/web-optimized files'
            }
          </p>
        </div>
        {!isSignedIn ? (
          <Button
            variant="default"
            size="sm"
            iconName="LogIn"
            iconPosition="left"
            onClick={handleConnect}
            loading={isConnecting}>
            Sign In
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={loadRootFolders}
            loading={loading}>
            Refresh
          </Button>
        )}
      </div>
      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive rounded-lg flex items-start gap-2">
          <Icon name="AlertCircle" size={16} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
          <span className="text-sm text-destructive">{error}</span>
        </div>
      )}
      {isSignedIn && (
        <div className="bg-card border border-border rounded-xl p-4 max-h-96 overflow-y-auto">
          {loading && folders?.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader2" size={24} className="animate-spin text-primary" />
              <span className="ml-2 text-sm text-muted-foreground">Loading folders...</span>
            </div>
          ) : folders?.length > 0 ? (
            folders?.map((folder) => renderFolder(folder))
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="FolderOpen" size={48} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No folders found</p>
            </div>
          )}
        </div>
      )}
      {!isSignedIn && (
        <div className="bg-muted border border-border rounded-xl p-6 text-center">
          <Icon name="Cloud" size={48} className="mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-muted-foreground mb-4">
            Sign in to Google Drive to browse and select folders
          </p>
        </div>
      )}
      {selectedFolders?.[folderType] &&
      <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary rounded-lg">
          <Icon name="CheckCircle2" size={16} color="var(--color-primary)" />
          <span className="text-sm text-foreground">
            Selected: <span className="font-medium">{selectedFolders?.[`${folderType}Name`]}</span>
          </span>
        </div>
      }
    </div>
  );

};

export default GoogleDriveFolderBrowser;